import { useEffect } from 'react';
import './WoodHammerAnimation.css';

/**
 * PROFESSIONAL WOOD HAMMER ANIMATION
 * Grass-type physical move - slams opponent with a massive wooden body
 * 
 * Key Features:
 * - Grass-type physical attack (120 base power, 100% accuracy)
 * - User takes 1/3 recoil damage
 * - Devastating nature-powered slam with leaf and wood debris
 * - Professional gaming quality with heavy impact and recoil
 */
export default function WoodHammerAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Nature Power Buildup (0-400ms)
    playerEl.classList.add('wood-hammer-user-charge');

    const natureAura = document.createElement('div');
    natureAura.className = 'wood-hammer-nature-aura';
    natureAura.style.left = `${playerX - 70}px`;
    natureAura.style.top = `${playerY - 70}px`;
    container.appendChild(natureAura);
    elements.push(natureAura);

    // Swirling leaves around player
    for (let i = 0; i < 10; i++) {
      const leaf = document.createElement('div');
      leaf.className = 'wood-hammer-leaf';
      const angle = (i / 10) * 360;
      const radius = 60 + (i % 3) * 10;
      leaf.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 6}px`;
      leaf.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 6}px`;
      leaf.style.animationDelay = `${i * 35}ms`;
      container.appendChild(leaf);
      elements.push(leaf);
    }

    // PHASE 2: Charging Slam (400-800ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('wood-hammer-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Green energy trail
      const trail = document.createElement('div');
      trail.className = 'wood-hammer-trail';
      trail.style.left = `${playerX - 10}px`;
      trail.style.top = `${playerY - 8}px`;
      trail.style.width = `${Math.hypot(dx, dy) * 0.85}px`;
      trail.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
      trail.style.transformOrigin = 'left center';
      container.appendChild(trail);
      elements.push(trail);

      // Leaf particles along path
      for (let i = 0; i < 10; i++) {
        const pTimer = setTimeout(() => {
          const p = document.createElement('div');
          p.className = 'wood-hammer-path-leaf';
          const progress = i / 10;
          p.style.left = `${playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 25 - 5}px`;
          p.style.top = `${playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 25 - 5}px`;
          container.appendChild(p);
          elements.push(p);
        }, i * 30);
        elements.push({ timer: pTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Heavy Impact (800-1300ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('wood-hammer-opponent-hit');
      playerEl.classList.remove('wood-hammer-user-dash');

      // Massive wood/nature impact
      const impact = document.createElement('div');
      impact.className = 'wood-hammer-impact';
      impact.style.left = `${opponentX - 100}px`;
      impact.style.top = `${opponentY - 100}px`;
      container.appendChild(impact);
      elements.push(impact);

      // Shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'wood-hammer-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Wood splinter debris
      for (let i = 0; i < 18; i++) {
        const splinterAngle = (i / 18) * 360;
        const splinterDist = 50 + Math.random() * 50;
        const splinter = document.createElement('div');
        splinter.className = 'wood-hammer-splinter';
        splinter.style.left = `${opponentX - 3}px`;
        splinter.style.top = `${opponentY - 3}px`;
        splinter.style.setProperty('--scatter-x', `${Math.cos(splinterAngle * Math.PI / 180) * splinterDist}px`);
        splinter.style.setProperty('--scatter-y', `${Math.sin(splinterAngle * Math.PI / 180) * splinterDist}px`);
        splinter.style.transform = `rotate(${Math.random() * 360}deg)`;
        splinter.style.animationDelay = `${i * 15}ms`;
        container.appendChild(splinter);
        elements.push(splinter);
      }

      // Leaf burst
      for (let i = 0; i < 12; i++) {
        const leafBurst = document.createElement('div');
        leafBurst.className = 'wood-hammer-burst-leaf';
        const lAngle = (i / 12) * 360;
        const lDist = 60 + Math.random() * 40;
        leafBurst.style.left = `${opponentX - 5}px`;
        leafBurst.style.top = `${opponentY - 5}px`;
        leafBurst.style.setProperty('--scatter-x', `${Math.cos(lAngle * Math.PI / 180) * lDist}px`);
        leafBurst.style.setProperty('--scatter-y', `${Math.sin(lAngle * Math.PI / 180) * lDist}px`);
        leafBurst.style.animationDelay = `${i * 20}ms`;
        container.appendChild(leafBurst);
        elements.push(leafBurst);
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Recoil (1300-1700ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('wood-hammer-user-charge');
      playerEl.classList.add('wood-hammer-user-recoil');

      const recoilFlash = document.createElement('div');
      recoilFlash.className = 'wood-hammer-recoil-flash';
      recoilFlash.style.left = `${playerX - 40}px`;
      recoilFlash.style.top = `${playerY - 40}px`;
      container.appendChild(recoilFlash);
      elements.push(recoilFlash);
    }, 1300);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('wood-hammer-user-charge', 'wood-hammer-user-dash', 'wood-hammer-user-recoil');
      opponentEl.classList.remove('wood-hammer-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1700);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('wood-hammer-user-charge', 'wood-hammer-user-dash', 'wood-hammer-user-recoil');
      opponentEl.classList.remove('wood-hammer-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

WoodHammerAnimation.moveType = 'grass';
